import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';

function formatRelativeTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Type → dot colour + badge style + readable label
const TYPE_STYLE = {
  workflow_rejected:  { dot: 'bg-red-500',    badge: 'bg-red-50 text-red-700 border-red-100',          label: 'Rejected' },
  rejected:           { dot: 'bg-red-500',    badge: 'bg-red-50 text-red-700 border-red-100',          label: 'Rejected' },
  workflow_approved:  { dot: 'bg-green-500',  badge: 'bg-green-50 text-green-700 border-green-100',    label: 'Approved' },
  approved:           { dot: 'bg-green-500',  badge: 'bg-green-50 text-green-700 border-green-100',    label: 'Approved' },
  workflow_submitted: { dot: 'bg-blue-500',   badge: 'bg-blue-50 text-blue-700 border-blue-100',       label: 'Submitted' },
  submitted:          { dot: 'bg-blue-500',   badge: 'bg-blue-50 text-blue-700 border-blue-100',       label: 'Submitted' },
  workflow_validated: { dot: 'bg-purple-500', badge: 'bg-purple-50 text-purple-700 border-purple-100', label: 'Validated' },
  validated:          { dot: 'bg-purple-500', badge: 'bg-purple-50 text-purple-700 border-purple-100', label: 'Validated' },
  system:             { dot: 'bg-gray-400',   badge: 'bg-gray-100 text-gray-600 border-gray-200',      label: 'System' },
};

function typeStyle(type) {
  return TYPE_STYLE[type] ?? TYPE_STYLE.system;
}

const NotificationIcon = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const {
    notifications, notifLoading, unreadCount,
    markAsRead, markAllRead,
  } = useNotifications();

  // Prevent background scroll when panel is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  function handleCardClick(n) {
    if (!n.is_read) markAsRead(n.uuid);
    if (n.route) navigate(n.route);
    setIsOpen(false);
  }

  return (
    <>
      {/* ── Bell button ──────────────────────────────────────────────── */}
      <button
        title="Notifications"
        onClick={() => setIsOpen(true)}
        className="relative w-8 h-8 flex items-center justify-center rounded-full text-gray-400
                   hover:text-gray-600 hover:bg-gray-100 transition-all duration-150"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px]
                           font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Backdrop ─────────────────────────────────────────────────── */}
      <div
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-black/30 z-[300] transition-opacity duration-300
                    ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      />

      {/* ── Off-canvas panel (slides from right) ─────────────────────── */}
      <div className={`
        fixed top-0 right-0 h-full w-[360px] max-w-[95vw] bg-white shadow-2xl z-[301]
        flex flex-col transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100
                        bg-gradient-to-r from-green-50 to-white shrink-0">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-700" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <h2 className="text-sm font-bold text-gray-800">Notifications</h2>
            {unreadCount > 0 && (
              <span className="text-[10px] font-bold bg-red-500 text-white rounded-full
                               px-1.5 py-0.5 leading-none">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
          {notifLoading ? (
            <div className="flex flex-col gap-3 p-5">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-gray-200 mt-2 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                    <div className="h-2 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 px-6 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">You're all caught up</p>
              <p className="text-xs text-gray-400">No notifications at this time</p>
            </div>
          ) : (
            notifications.map(n => {
              const style = typeStyle(n.type);
              return (
                <button
                  key={n.uuid}
                  onClick={() => handleCardClick(n)}
                  className={`
                    w-full text-left px-5 py-4 flex items-start gap-3
                    hover:bg-gray-50 transition-colors duration-100 group
                    ${!n.is_read ? 'bg-blue-50/20' : ''}
                  `}
                >
                  {/* Unread / read dot */}
                  <span className={`mt-2 w-2 h-2 rounded-full shrink-0
                                    ${n.is_read ? 'bg-gray-200' : style.dot}`} />

                  <div className="flex-1 min-w-0">
                    {/* Module + type badges */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <span className="text-[10px] font-semibold bg-green-50 text-green-700
                                       border border-green-100 rounded px-1.5 py-0.5 leading-none">
                        {n.moduleLabel}
                      </span>
                      <span className={`text-[10px] font-semibold border rounded
                                        px-1.5 py-0.5 leading-none ${style.badge}`}>
                        {style.label}
                      </span>
                    </div>

                    {/* Title */}
                    <p className={`text-sm font-semibold leading-snug
                                   ${n.is_read ? 'text-gray-500' : 'text-gray-900'}`}>
                      {n.title}
                    </p>

                    {/* Message */}
                    {n.message && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                        {n.message}
                      </p>
                    )}

                    {/* Timestamp */}
                    <p className="text-[10px] text-gray-400 mt-1.5">
                      {formatRelativeTime(n.time)}
                    </p>
                  </div>

                  {/* Navigate chevron */}
                  <svg
                    className="w-4 h-4 text-gray-200 group-hover:text-gray-400 shrink-0 mt-1 transition-colors"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        {!notifLoading && notifications.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 shrink-0">
            <span className="text-xs text-gray-400">
              {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                className="text-xs font-semibold text-green-700 hover:text-green-800 px-3 py-1.5
                           rounded-lg hover:bg-green-50 transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationIcon;
